import { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { useToast } from "@/components/ui/use-toast"
import { queryClient } from "@/lib/react-query"
import { client, validateClientProperty } from "@/lib/ts-rest"
import { useGetIntegrations } from "../../lib/hooks"
import { CheckIcon, XIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { trackEvent } from "@/lib/log"

const mainPrayerTypes = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"] as const
type MainPrayerType = (typeof mainPrayerTypes)[number]

const days = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const
type Day = (typeof days)[number]

type AthanNotificationSchedule = {
  prayerTimeType: MainPrayerType
  day: Day
}

export function AthanNotificationSettings({
  integrationId,
}: {
  integrationId: number
}) {
  const { toast } = useToast()
  const { data: integrations } = useGetIntegrations()

  const integration = integrations?.body.integrations.find(
    (item) => item.integration.id === integrationId
  )

  const [schedules, setSchedules] = useState<AthanNotificationSchedule[]>([])
  const savedSchedules = useRef<AthanNotificationSchedule[]>([])

  useEffect(() => {
    if (integration?.integration.athanNotificationSchedules) {
      const existingSchedules =
        integration.integration.athanNotificationSchedules
          .map((schedule: any) => ({
            prayerTimeType: schedule.prayerTimeType as MainPrayerType,
            day: schedule.day as Day,
          }))
          .filter(
            (schedule: AthanNotificationSchedule) =>
              days.includes(schedule.day) &&
              mainPrayerTypes.includes(schedule.prayerTimeType)
          )

      setSchedules(existingSchedules)
      savedSchedules.current = existingSchedules
    }
  }, [integration])

  const isDirty = (() => {
    if (schedules.length !== savedSchedules.current.length) return true
    const toKey = (s: AthanNotificationSchedule) => `${s.day}:${s.prayerTimeType}`
    const currentKeys = new Set(schedules.map(toKey))
    return !savedSchedules.current.every((s) => currentKeys.has(toKey(s)))
  })()

  const { mutate: updateIntegration, isPending } =
    client.updateIntegration.useMutation({
      onSuccess: () => {
        savedSchedules.current = [...schedules]
        queryClient.invalidateQueries({
          queryKey: [validateClientProperty("getIntegrations")],
        })
        toast({ title: "Athan notification settings updated." })
      },
      onError: () => {
        toast({
          title: "Error updating settings",
          description: "Please try again.",
          variant: "destructive",
        })
      },
    })

  // Helper function to check if a prayer is enabled for a day
  const isPrayerEnabledForDay = (day: Day, prayer: MainPrayerType): boolean => {
    return schedules.some((s) => s.day === day && s.prayerTimeType === prayer)
  }

  // Check if all prayers are enabled for a given day
  const isAllPrayersEnabledForDay = (day: Day): boolean => {
    return mainPrayerTypes.every((prayer) => isPrayerEnabledForDay(day, prayer))
  }

  // Check if a prayer is enabled for all days
  const isPrayerEnabledForAllDays = (prayer: MainPrayerType): boolean => {
    return days.every((day) => isPrayerEnabledForDay(day, prayer))
  }

  // Toggle all prayers for a specific day
  const toggleAllPrayersForDay = (day: Day) => {
    const allEnabled = isAllPrayersEnabledForDay(day)

    if (allEnabled) {
      // Remove all schedules for this day
      setSchedules((prev) => prev.filter((s) => s.day !== day))
    } else {
      // Add all prayers for this day (remove existing ones first to avoid duplicates)
      const newSchedules = schedules.filter((s) => s.day !== day)
      for (const prayer of mainPrayerTypes) {
        newSchedules.push({ day, prayerTimeType: prayer })
      }
      setSchedules(newSchedules)
    }
  }

  // Toggle a prayer type for all days
  const togglePrayerForAllDays = (prayer: MainPrayerType) => {
    const enabledForAllDays = isPrayerEnabledForAllDays(prayer)

    if (enabledForAllDays) {
      // Remove this prayer for all days
      setSchedules((prev) => prev.filter((s) => s.prayerTimeType !== prayer))
    } else {
      // Add this prayer for all days (remove existing ones first to avoid duplicates)
      const newSchedules = schedules.filter((s) => s.prayerTimeType !== prayer)
      for (const day of days) {
        newSchedules.push({ day, prayerTimeType: prayer })
      }
      setSchedules(newSchedules)
    }
  }

  // Toggle individual prayer for a day
  const togglePrayerForDay = (day: Day, prayer: MainPrayerType) => {
    const isEnabled = isPrayerEnabledForDay(day, prayer)

    if (isEnabled) {
      // Remove this specific schedule
      setSchedules((prev) =>
        prev.filter((s) => !(s.day === day && s.prayerTimeType === prayer))
      )
    } else {
      // Add this specific schedule
      setSchedules((prev) => [...prev, { day, prayerTimeType: prayer }])
    }
  }

  const handleSave = () => {
    trackEvent("saved athan schedule", { integrationId, enabledCount: schedules.length })
    updateIntegration({
      params: { integrationId: String(integrationId) },
      body: {
        integration: {},
        athanNotificationSchedules: schedules,
      },
    })
  }

  return (
    <div className="space-y-6">
      <div className="text-sm overflow-x-auto w-full">
        <table className="border-separate border-spacing-0 border border-gray-200 bg-white rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-gray-50">
              <th className="border-r border-b border-gray-200 p-3 text-left font-medium text-gray-900 rounded-tl-lg"></th>
              {days.map((day, index) => {
                const allEnabled = isAllPrayersEnabledForDay(day)
                const isLastDay = index === days.length - 1
                return (
                  <th
                    key={day}
                    className={cn(
                      "border-r border-b border-gray-200 p-3 text-center font-medium text-gray-900 min-w-[80px] cursor-pointer transition-colors hover:bg-gray-100",
                      isLastDay && "border-r-0 rounded-tr-lg"
                    )}
                    onClick={() => toggleAllPrayersForDay(day)}
                  >
                    {day.slice(0, 3)}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {mainPrayerTypes.map((prayer, prayerIndex) => {
              const enabledForAllDays = isPrayerEnabledForAllDays(prayer)
              const isLastPrayer = prayerIndex === mainPrayerTypes.length - 1
              return (
                <tr key={prayer} className="hover:bg-gray-50">
                  <td
                    className={cn(
                      "border-r border-b border-gray-200 p-3 font-medium text-gray-900 cursor-pointer transition-colors hover:bg-gray-100",
                      isLastPrayer && "border-b-0 rounded-bl-lg"
                    )}
                    onClick={() => togglePrayerForAllDays(prayer)}
                  >
                    {prayer}
                  </td>
                  {days.map((day, dayIndex) => {
                    const isEnabled = isPrayerEnabledForDay(day, prayer)
                    const isLastDay = dayIndex === days.length - 1
                    return (
                      <td
                        key={`${day}-${prayer}`}
                        className={cn(
                          "border-r border-b border-gray-200 p-0 text-center cursor-pointer transition-colors",
                          isEnabled
                            ? "bg-green-50 hover:bg-green-100"
                            : "bg-red-50 hover:bg-red-100",
                          isLastDay && "border-r-0",
                          isLastPrayer && "border-b-0",
                          isLastDay && isLastPrayer && "rounded-br-lg"
                        )}
                        onClick={() => togglePrayerForDay(day, prayer)}
                      >
                        <div className="w-full h-full p-4 flex items-center justify-center">
                          {isEnabled ? (
                            <CheckIcon
                              className={cn("w-5 h-5 text-green-700")}
                            />
                          ) : (
                            <XIcon className={cn("w-5 h-5 text-red-700")} />
                          )}
                        </div>
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="flex gap-3">
        <Button onClick={handleSave} disabled={!isDirty || isPending}>
          {isPending ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  )
}
